import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { categoriesAPI, listingsAPI } from '../utils/api';
import { SlidersHorizontal, X, Heart } from 'lucide-react';
import CategoryIcon from '../components/CategoryIcon';
import HScroller from '../components/HScroller';
import ResponsiveImage from '../components/ResponsiveImage';
import DealerBadge from '../components/DealerBadge';
import TrustedSellerBadge from '../components/TrustedSellerBadge';
import useScrollRestore, { saveFeedScroll } from '../hooks/useScrollRestore';
import { useAuth } from '../context/AuthContext';
import useWishlistToggle from '../hooks/useWishlistToggle';

const CategoryBrowse = () => {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const subId = searchParams.get('sub');
  const { user } = useAuth();
  const { isSaved: isWishlisted, toggle: toggleWishlist } = useWishlistToggle(user);
  const [category, setCategory] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSub, setActiveSub] = useState(subId || 'all');
  const [filters, setFilters] = useState({ minPrice: '', maxPrice: '', sort: 'newest' });
  const [showFilters, setShowFilters] = useState(false);

  // Fix #6: restore scroll on Back. Key includes sub-filter so different filters keep their own positions.
  useScrollRestore(`category:${categoryId}:${activeSub}`, { ready: listings.length > 0 });

  useEffect(() => {
    loadData();
  }, [categoryId]);

  useEffect(() => {
    if (subId) setActiveSub(subId);
  }, [subId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, listRes] = await Promise.all([
        categoriesAPI.getAll(),
        listingsAPI.getAll({ category_id: categoryId })
      ]);
      const cat = catRes.data.find(c => c.category_id === categoryId);
      setCategory(cat);
      setListings(listRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const subs = category?.subcategories || [];
  const filtered = listings
    .filter(l => {
      if (activeSub !== 'all' && l.subcategory_id !== activeSub) return false;
      if (filters.minPrice && l.price < parseFloat(filters.minPrice)) return false;
      if (filters.maxPrice && l.price > parseFloat(filters.maxPrice)) return false;
      return true;
    })
    .sort((a, b) => {
      if (filters.sort === 'price_low') return a.price - b.price;
      if (filters.sort === 'price_high') return b.price - a.price;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-[#1800ad]"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-4">
          <Link to="/" className="text-[#6B7280] hover:text-[#1800ad] text-xs sm:text-sm font-medium mb-3 inline-block" data-testid="back-home">&larr; Back to Home</Link>
          <div className="flex items-center gap-3 mb-4">
            {category && (
              <div className="p-1.5 rounded-xl" style={{ backgroundColor: category.color }}>
                <CategoryIcon category={categoryId} size={36} />
              </div>
            )}
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-[#0A0B10]" data-testid="category-title">{category?.name || 'Category'}</h1>
              <p className="text-xs sm:text-sm text-[#6B7280]">{filtered.length} listing{filtered.length !== 1 ? 's' : ''}</p>
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className="ml-auto p-2.5 rounded-xl bg-gray-100 text-[#6B7280] hover:bg-gray-200" data-testid="filter-toggle">
              <SlidersHorizontal size={18} />
            </button>
          </div>

          {/* Subcategory tabs */}
          {subs.length > 0 && (
            <HScroller ariaLabel="subcategories" testId="subcategory-tabs">
              <div className="flex gap-2 pb-1 px-1 md:px-10">
                <button onClick={() => setActiveSub('all')} data-testid="sub-all"
                  className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${activeSub === 'all' ? 'bg-[#1800ad] text-white' : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'}`}>
                  All
                </button>
                {subs.map(sub => (
                  <button key={sub.id} onClick={() => setActiveSub(sub.id)} data-testid={`sub-${sub.id}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${activeSub === sub.id ? 'bg-[#1800ad] text-white' : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'}`}>
                    {sub.name}
                  </button>
                ))}
              </div>
            </HScroller>
          )}

          {showFilters && (
            <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="filters">
              <input type="number" placeholder="Min ₹" value={filters.minPrice} onChange={e => setFilters({...filters, minPrice: e.target.value})}
                className="w-24 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" />
              <input type="number" placeholder="Max ₹" value={filters.maxPrice} onChange={e => setFilters({...filters, maxPrice: e.target.value})}
                className="w-24 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" />
              <select value={filters.sort} onChange={e => setFilters({...filters, sort: e.target.value})}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs bg-white">
                <option value="newest">Newest</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
              </select>
              <button onClick={() => setFilters({ minPrice: '', maxPrice: '', sort: 'newest' })} className="text-xs text-red-500"><X size={14} /></button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg font-bold text-[#0A0B10] mb-2">No listings yet</p>
            <p className="text-sm text-[#6B7280] mb-4">Be the first to post in this category</p>
            <Link to="/post-ad" className="inline-block bg-[#ff8500] text-white rounded-full px-6 py-2 text-sm font-bold">Post Ad</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-5 items-start">
            {filtered.map(listing => (
              <div key={listing.listing_id} className="relative">
                {/* iter118 — shared wishlist heart on every listing card */}
                <button
                  type="button"
                  onClick={(e) => toggleWishlist(e, listing.listing_id)}
                  aria-label={isWishlisted(listing.listing_id) ? 'Remove from wishlist' : 'Add to wishlist'}
                  data-testid={`wishlist-toggle-${listing.listing_id}`}
                  className="absolute top-2 right-2 z-10 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center hover:bg-white active:scale-90 transition-all"
                >
                  <Heart
                    size={17}
                    strokeWidth={2.2}
                    className={isWishlisted(listing.listing_id) ? 'text-red-500 fill-red-500' : 'text-gray-500'}
                  />
                </button>
                <Link to={`/listing/${listing.listing_id}`} data-testid={`listing-${listing.listing_id}`}
                  onClick={() => saveFeedScroll(`category:${categoryId}:${activeSub}`)}
                  className={`brand-gradient-border-card group flex flex-col rounded-xl sm:rounded-2xl p-2 sm:p-3.5 ${listing.is_dealer_listing ? 'dealer-card' : ''}`}>
                  {listing.images?.length > 0 ? (
                    <ResponsiveImage
                      path={listing.images[0]}
                      alt={listing.title}
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 25vw"
                      className="w-full h-28 sm:h-44 object-cover rounded-lg sm:rounded-xl mb-2 sm:mb-3"
                    />
                  ) : (
                    <div className="w-full h-28 sm:h-44 bg-gray-100 rounded-lg sm:rounded-xl mb-2 sm:mb-3 flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No Image</span>
                    </div>
                  )}
                  <h3 className="text-xs sm:text-base font-bold text-[#0A0B10] mb-1 line-clamp-2">{listing.title}</h3>
                  <p className="text-sm sm:text-xl font-bold text-[#1800ad] mb-1">{'\u20B9'}{listing.price?.toLocaleString()}</p>
                  <p className="text-[10px] sm:text-xs text-[#9CA3AF]">{listing.location}</p>
                  {(listing.is_dealer_listing || listing.is_trusted_seller) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {listing.is_dealer_listing && <DealerBadge testId={`dealer-badge-${listing.listing_id}`} />}
                      {listing.is_trusted_seller && !listing.is_dealer_listing && (
                        <TrustedSellerBadge size="sm" short testId={`trusted-badge-${listing.listing_id}`} />
                      )}
                    </div>
                  )}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryBrowse;
